import { useState } from "react";
import { useGetOne, useRecordContext } from "ra-core";
import { Card, CardContent } from "@/components/ui/card";
import {
  ArrowLeft,
  Send,
  Check,
  X,
  FileText,
  Calendar,
  Loader2,
} from "lucide-react";
import { EstimateStatusBadge } from "./EstimateStatusBadge";
import { EstimateLineItems } from "./EstimateLineItems";
import { DeliveryStatusBar } from "./DeliveryStatusBar";
import { SendEstimateDialog } from "./SendEstimateDialog";
import { useEstimateActions } from "./useEstimateActions";
import type { BldrOsContact } from "../types";

interface Estimate {
  id: string;
  org_id: string;
  contact_id: string | number;
  estimate_number: string;
  status: string;
  title: string;
  description: string;
  notes: string;
  labor_total: number;
  material_total: number;
  other_total: number;
  subtotal: number;
  tax_rate: number;
  tax_amount: number;
  total: number;
  estimated_days: number | null;
  valid_until: string | null;
  sent_at: string | null;
  accepted_at: string | null;
  declined_at: string | null;
  viewed_at: string | null;
  delivery_method: string | null;
  recipient_phone: string | null;
  recipient_email: string | null;
  access_token: string | null;
  created_at: string;
  updated_at: string;
}

interface EstimateDetailProps {
  estimateId: string;
  onBack: () => void;
}

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(amount);
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

export function EstimateDetail({ estimateId, onBack }: EstimateDetailProps) {
  const contact = useRecordContext<BldrOsContact>();
  const { data: estimate, isPending } = useGetOne<Estimate>("estimates", {
    id: estimateId,
  });
  const { accept, decline, isPending: actionPending } = useEstimateActions();
  const [sendOpen, setSendOpen] = useState(false);

  if (isPending || !estimate) {
    return (
      <div className="py-8 text-center text-sm text-muted-foreground">
        Loading estimate…
      </div>
    );
  }

  const isEditable = estimate.status === "draft";
  const isSendable = estimate.status === "draft";
  const isActionable = estimate.status === "sent";

  // Resolve contact info for SendDialog
  const contactPhone =
    estimate.recipient_phone ||
    contact?.phone_primary ||
    (contact?.phone_jsonb?.[0] as { number?: string })?.number ||
    null;
  const contactEmail =
    estimate.recipient_email ||
    (contact?.email_jsonb?.[0] as { email?: string })?.email ||
    null;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button
          onClick={onBack}
          className="p-1.5 rounded hover:bg-gray-100 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
        </button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <FileText className="w-4 h-4 text-muted-foreground" />
            <span className="text-sm font-mono text-muted-foreground">
              {estimate.estimate_number}
            </span>
            <EstimateStatusBadge status={estimate.status} />
          </div>
          <h3 className="text-lg font-semibold truncate mt-0.5">
            {estimate.title || "Untitled Estimate"}
          </h3>
        </div>

        {/* Actions */}
        <div className="flex gap-2 shrink-0">
          {isSendable && (
            <button
              onClick={() => setSendOpen(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 transition-colors"
            >
              <Send className="w-3.5 h-3.5" />
              Send
            </button>
          )}
          {isActionable && (
            <>
              <button
                onClick={() => accept(estimate)}
                disabled={actionPending}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium text-white bg-green-600 hover:bg-green-700 transition-colors disabled:opacity-50"
              >
                {actionPending ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <Check className="w-3.5 h-3.5" />
                )}
                Accept
              </button>
              <button
                onClick={() => decline(estimate)}
                disabled={actionPending}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium text-red-700 bg-red-50 hover:bg-red-100 transition-colors disabled:opacity-50"
              >
                {actionPending ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <X className="w-3.5 h-3.5" />
                )}
                Decline
              </button>
            </>
          )}
        </div>
      </div>

      {/* Delivery status bar */}
      <DeliveryStatusBar
        status={estimate.status}
        sentAt={estimate.sent_at}
        viewedAt={estimate.viewed_at}
        acceptedAt={estimate.accepted_at}
        declinedAt={estimate.declined_at}
        deliveryMethod={estimate.delivery_method}
        recipientPhone={estimate.recipient_phone}
        recipientEmail={estimate.recipient_email}
      />

      {/* Description & notes */}
      {(estimate.description || estimate.notes) && (
        <Card>
          <CardContent className="p-4 space-y-2">
            {estimate.description && (
              <p className="text-sm">{estimate.description}</p>
            )}
            {estimate.notes && (
              <p className="text-sm text-muted-foreground italic">
                {estimate.notes}
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Metadata row */}
      <div className="flex flex-wrap gap-4 text-xs text-muted-foreground">
        <span className="flex items-center gap-1">
          <Calendar className="w-3 h-3" />
          Created {formatDate(estimate.created_at)}
        </span>
        {estimate.valid_until && (
          <span>Valid until {formatDate(estimate.valid_until)}</span>
        )}
        {estimate.estimated_days && (
          <span>Est. {estimate.estimated_days} days</span>
        )}
      </div>

      {/* Line items */}
      <div>
        <h4 className="text-sm font-semibold mb-3">Line Items</h4>
        <EstimateLineItems
          estimateId={estimate.id}
          readOnly={!isEditable}
        />
      </div>

      {/* Totals */}
      <Card>
        <CardContent className="p-4">
          <div className="space-y-1.5 max-w-xs ml-auto">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Labor</span>
              <span className="tabular-nums">
                {formatCurrency(Number(estimate.labor_total))}
              </span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Materials</span>
              <span className="tabular-nums">
                {formatCurrency(Number(estimate.material_total))}
              </span>
            </div>
            {Number(estimate.other_total) > 0 && (
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Other</span>
                <span className="tabular-nums">
                  {formatCurrency(Number(estimate.other_total))}
                </span>
              </div>
            )}
            <div className="border-t pt-1.5 flex justify-between text-sm">
              <span className="text-muted-foreground">Subtotal</span>
              <span className="tabular-nums">
                {formatCurrency(Number(estimate.subtotal))}
              </span>
            </div>
            {Number(estimate.tax_amount) > 0 && (
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">
                  Tax ({(Number(estimate.tax_rate) * 100).toFixed(1)}%)
                </span>
                <span className="tabular-nums">
                  {formatCurrency(Number(estimate.tax_amount))}
                </span>
              </div>
            )}
            <div className="border-t pt-1.5 flex justify-between text-base font-bold">
              <span>Total</span>
              <span className="tabular-nums">
                {formatCurrency(Number(estimate.total))}
              </span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Send dialog */}
      <SendEstimateDialog
        open={sendOpen}
        onClose={() => setSendOpen(false)}
        estimateId={estimate.id}
        estimateNumber={estimate.estimate_number}
        contactPhone={contactPhone}
        contactEmail={contactEmail}
      />
    </div>
  );
}
